"use client";

import { useEffect, useRef } from "react";
import { sendDiscoveryEventBeacon } from "@/lib/discoveryEventBeacon";
import type { DiscoveryEventKey } from "@/app/actions/trackDiscoveryEventUtils";

/**
 * Fires `event` once when the visitor scrolls past `threshold` (0-1) of the
 * page's height. Uses an IntersectionObserver against a positioned sentinel
 * rather than polling scroll position on every `scroll` tick, matching the
 * depth-detection pattern already used elsewhere in this app (e.g.
 * ProgramSubjectNav's section tracking, LearningFormatParts' StickyFormatChrome).
 */
export function useScrollDepthEvent(event: DiscoveryEventKey, properties?: Record<string, unknown>, threshold = 0.5) {
  const firedRef = useRef(false);
  useEffect(() => {
    firedRef.current = false;
    const sentinel = document.createElement("div");
    sentinel.style.position = "absolute";
    sentinel.style.left = "0";
    sentinel.style.width = "1px";
    sentinel.style.height = "1px";
    sentinel.style.pointerEvents = "none";
    sentinel.setAttribute("aria-hidden", "true");
    document.body.appendChild(sentinel);
    const positionSentinel = () => {
      sentinel.style.top = `${document.documentElement.scrollHeight * threshold}px`;
    };
    positionSentinel();

    // Requires a real scroll before counting an intersection as "reached
    // this depth" — without this, a page shorter than 2x the viewport
    // height has the sentinel inside the initial viewport, so it fires on
    // mount with zero actual scrolling.
    //
    // IntersectionObserver only calls back on visibility *crossings*, not on
    // every scroll tick. If the sentinel is already visible when observation
    // starts, no further crossing ever happens — so `hasScrolled` can't just
    // be read inside the observer callback, since nothing would re-run it
    // once the visitor actually scrolls. `isIntersecting` tracks the
    // last-known visibility so the scroll listener can also trigger the fire
    // check, not only the observer.
    let hasScrolled = false;
    let isIntersecting = false;
    const tryFire = () => {
      if (isIntersecting && hasScrolled && !firedRef.current) {
        firedRef.current = true;
        sendDiscoveryEventBeacon(event, properties);
        cleanup();
      }
    };
    const onScroll = () => {
      hasScrolled = true;
      tryFire();
    };
    window.addEventListener("scroll", onScroll, {
      passive: true
    });
    const intersectionObserver = new IntersectionObserver(([entry]) => {
      isIntersecting = entry?.isIntersecting ?? false;
      tryFire();
    });
    intersectionObserver.observe(sentinel);

    // Re-measures as content loads in (images, async sections) and shifts
    // the page's total scrollable height.
    const resizeObserver = new ResizeObserver(positionSentinel);
    resizeObserver.observe(document.documentElement);
    function cleanup() {
      window.removeEventListener("scroll", onScroll);
      intersectionObserver.disconnect();
      resizeObserver.disconnect();
      sentinel.remove();
    }
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally fire only once per mount
  }, []);
}